import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Pagination from '@mui/material/Pagination';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CandidateProfile from '../../components/LovableComponents/CandidateProfile';
import CandidateCard from '../../components/LovableComponents/CandidateCard';
import { useGetCoresignalCandidateStatusQuery, useGetCoresignalCandidateViewedQuery, usePostCoresignalCandidateDataMutation } from '../../redux/coresignalEmployer';
// import Header from '../components/Header';
// import CandidateCard from '../components/CandidateCard';
// import CandidateProfile from '../components/CandidateProfile';

// Transform API data to match the expected format for CandidateCard
const transformCandidateData = (apiData) => {
  if (!apiData) return [];

  return apiData.map((candidate, index) => {
    const latestExperience = candidate.data.experience?.[0];
    const education = candidate.data.education?.[0];

    return {
      id: candidate.data.id || `candidate-${index}`,
      name: candidate.data.full_name || 'N/A',
      photo: candidate.data.picture_url || 'https://via.placeholder.com/150',
      title: latestExperience?.position_title || 'Professional',
      experience: candidate.data.total_experience_duration_months
        ? Math.round(candidate.data.total_experience_duration_months / 12)
        : 0,
      location: candidate.data.location_full || 'Location not specified',
      lastActive: 'Recently',
      fitScore: 'good',
      fitReason: 'Matched based on profile data',
      education: candidate.data.education?.map(edu => ({
        degree: edu.degree || 'Education',
        institution: edu.institution_name || 'Institution',
        year: edu.date_from_year ? String(edu.date_from_year) : 'N/A'
      })) || [],
      workHistory: candidate.data.experience?.map(exp => ({
        title: exp.position_title || 'Role',
        company: exp.company_name || 'Company',
        duration: exp.date_from_year ? `${exp.date_from_year} - ${exp.date_to_year || 'Present'}` : 'N/A',
        description: exp.description || ''
      })) || [],
      skills: candidate.data.inferred_skills || [],
      certifications: [],
      lastUpdated: new Date(candidate.updated_at || candidate.created_at).toLocaleDateString(),
      contactRevealed: false,
      isViewed: true,
      linkedinUrl: candidate.data.linkedin_url || '#',
      matchingAttributes: ['Profile Match'],
      highestEducation: education?.degree || 'N/A'
    };
  });
};

const ITEMS_PER_PAGE = 6;

const ViewedProfiles = () => {

  const { data: coresignalCandidateViewed, isLoading } = useGetCoresignalCandidateViewedQuery();
  const [postCandidateData] = usePostCoresignalCandidateDataMutation();
  const { 
      data: coresignalCandidateStatus, 
      isLoading: isLoadingStatus, 
      isError: isStatusError, 
      error: statusError, 
      refetch: coresignalCandidateStatusRefetch
    } = useGetCoresignalCandidateStatusQuery();

  const [page, setPage] = useState(1);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [shortlisted, setShortlisted] = useState(
    () => coresignalCandidateStatus?.filter(c => c.status === 'shortlisted').map(c => parseInt(c.coresignal_candidate.coresignal_id, 10)) || []
  );
  const [rejected, setRejected] = useState(
    () => coresignalCandidateStatus?.filter(c => c.status === 'rejected').map(c => parseInt(c.coresignal_candidate.coresignal_id, 10)) || []
  );

  // Update states when coresignalCandidateStatus changes
  useEffect(() => {
    if (coresignalCandidateStatus) {
      setShortlisted(coresignalCandidateStatus
        .filter(c => c.status === 'shortlisted')
        .map(c => parseInt(c.coresignal_candidate.coresignal_id, 10))
      );
      setRejected(coresignalCandidateStatus
        .filter(c => c.status === 'rejected')
        .map(c => parseInt(c.coresignal_candidate.coresignal_id, 10))
      );
    }
  }, [coresignalCandidateStatus]);

  // Transform API data to match the expected format
  const viewedCandidates = transformCandidateData(coresignalCandidateViewed);

  const totalPages = Math.ceil(viewedCandidates.length / ITEMS_PER_PAGE);
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const displayedCandidates = viewedCandidates.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleShortlist = async (candidateId) => {
    try {
      const status = shortlisted.includes(candidateId) ? 'remove_from_shortlist' : 'shortlisted';
      await postCandidateData({
        candidate_id: candidateId,
        status
      }).unwrap();

      setShortlisted(prev =>
        prev.includes(candidateId)
          ? prev.filter(id => id !== candidateId)
          : [...prev, candidateId]
      );
    } catch (error) {
      console.error('Error updating shortlist:', error);
      // Optionally show error to user
    }
    coresignalCandidateStatusRefetch();
  };

  const handleReject = async (candidateId) => {
    try {
      const status = rejected.includes(candidateId) ? 'remove_from_rejected' : 'rejected';
      await postCandidateData({
        candidate_id: candidateId,
        status
      }).unwrap();

      setRejected(prev =>
        prev.includes(candidateId)
          ? prev.filter(id => id !== candidateId)
          : [...prev, candidateId]
      );
    } catch (error) {
      console.error('Error updating rejected list:', error);
      // Optionally show error to user
    }
    coresignalCandidateStatusRefetch();
  };

  return (
    <Box sx={{ minHeight: '93vh', bgcolor: 'grey.50' }}>
      {/* <Header sentInvites={12} repliesReceived={4} /> */}

      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 4 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3, color: 'text.primary' }}>
          Viewed Profiles
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          These are the candidate profiles you have previously viewed and are saved in the system.
        </Typography>

        {/* Candidate Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 2,
            minHeight: 'auto'
          }}
        >
          {(() => {
            if (isLoading) {
              return (
                <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4 }}>
                  <Typography>Loading candidates...</Typography>
                </Box>
              );
            }
            if (viewedCandidates.length === 0) {
              return (
                <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4 }}>
                  <Typography>No viewed candidates found.</Typography>
                </Box>
              );
            }
            return displayedCandidates.map((candidate) => (
              <CandidateCard
                key={candidate.id}
                candidate={candidate}
                isSelected={selectedCandidate?.id === candidate.id}
                onSelect={() => setSelectedCandidate(candidate)}
                onShortlist={() => handleShortlist(candidate.id)}
                onReject={() => handleReject(candidate.id)}
                isShortlisted={shortlisted.includes(candidate.id)}
              />
            ));
          })()}
        </Box>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Box>

      {/* Profile Side-Sheet Drawer */}
      <Drawer
        anchor="right"
        open={!!selectedCandidate}
        onClose={() => setSelectedCandidate(null)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 480, md: 520 } },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setSelectedCandidate(null)}>
            <CloseIcon />
          </IconButton>
        </Box>
        {selectedCandidate && (
          <Box sx={{ px: 2, pb: 3 }}>
            <CandidateProfile
              candidateId={selectedCandidate.id}
              onRevealContact={() => { }}
              isContactRevealed={selectedCandidate.contactRevealed}
            />
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default ViewedProfiles;
